import {
  AccordionContentGroup,
  ContentRoute,
  createMapper,
  RouteSlotInput,
} from '@caritas/ui';
import * as queryString from 'query-string';

import { useQueryParamString } from '../../commons/query-string';
import { isDefined } from '../../utils/is-defined';
import { mapHtmlBlock } from './HtmlBlock.mappers';
import { mapImageBlock } from './Image.mappers';

const mapAccordionItemContent = createMapper<
  Array<AccordionItemContentFragment>,
  RouteSlotInput<typeof AccordionContentGroup, 'items'>
>({
  BlockImage: mapImageBlock,
  BlockHtmlParagraph: mapHtmlBlock,
  BlockHtmlList: mapHtmlBlock,
});

export function mapAccordion(
  input: BlockAccordionFragment,
): RouteSlotInput<typeof ContentRoute, 'body', 'accordion'> {
  return {
    key: 'accordion',
    input: function useMapAccordion() {
      const [showCollapsible] = useQueryParamString('showCollapsible', '');
      let showCollapsibleItems: Array<string> = showCollapsible !== '' ? JSON.parse(showCollapsible) : [];
      return [
        {
          faqs:
            input.contentItems
              ?.map((item) => {
                return item
                  ? {
                      title: item.title || '',
                      itemPointerUpHandler: (title: string, openedItem: boolean) => {
                        if (openedItem) {
                          showCollapsibleItems.push(title);
                        } else {
                          showCollapsibleItems = showCollapsibleItems.filter(itemValue => itemValue !== title);
                        }
                        // We directly manipulate the window history here, without
                        // using the setter function from useQueryParamString()
                        // because we do not want any re-rendering. We just want
                        // that the accordion item collapsible feature to function
                        // as intended and only have the browser url query
                        // parameters updated. Re-rendering would just create more
                        // issues, and destroys the collapsible animation.
                        const parameters = queryString.parse(window.location.search);
                        const {
                          // eslint-disable-next-line @typescript-eslint/no-unused-vars
                          showCollapsible,
                          ... newParameters
                        } = parameters;
                        if (showCollapsibleItems.length > 0) {
                          newParameters['showCollapsible'] = JSON.stringify(showCollapsibleItems);
                        }

                        const newQuery = queryString.stringify(newParameters);
                        const urlSuffix = newQuery !== '' ? `?${newQuery}` : '';
                        // The reason why we set here null is that we have a GTM
                        // tag that checks the history state changes (for
                        // identifying when webforms are submitted) and if we
                        // make a call to replaceState() without removig the
                        // previouos state then the tag script will fire all the
                        // time when the collapsible title gets clicked.
                        window.history.replaceState(
                          null,
                          '',
                          `${window.location.pathname}${urlSuffix}`,
                        );
                      },
                      defaultOpen: showCollapsibleItems.includes(item.title),
                      content: {
                        items: item.content
                          ? mapAccordionItemContent(
                              item.content.filter(isDefined),
                            )
                          : [],
                      },
                    }
                  : undefined;
              })
              .filter(isDefined) || [],
        },
        200,
      ];
    }
  };
}
